<!-- DonutChart.vue -->
<template>
  <div class="chart-container">
    <canvas
      ref="canvas"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
    />
    <div v-if="hoveredData" class="tooltip" :style="tooltipStyle">
      <span
        class="tooltip-color"
        :style="{
          backgroundColor: STATUS_COLORS[hoveredData.Status] || '#ccc',
        }"
      ></span>
      {{ getStatusName(hoveredData.Status) }}: {{ hoveredData.Count }} ({{
        getPercentage(hoveredData.Count)
      }}%)
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch, nextTick, computed } from "vue";

// Определяем перечисление статусов
enum TaskStatus {
  Disabled = 0,
  Wait = 1,
  Queued = 2,
  Preparing = 3,
  RunWaiting = 4,
  Running = 5,
  Success = 6,
  Failed = 7,
  Removed = 8,
}

// Цвета для каждого статуса
const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.Disabled]: "#660000",
  [TaskStatus.Wait]: "#cc5500",
  [TaskStatus.Queued]: "#365d6f",
  [TaskStatus.Preparing]: "#cc8400",
  [TaskStatus.RunWaiting]: "#009a9a",
  [TaskStatus.Running]: "#002eb8",
  [TaskStatus.Success]: "#29a329",
  [TaskStatus.Failed]: "#cc0000",
  [TaskStatus.Removed]: "#7a7a7a",
};

// Названия статусов
const STATUS_NAMES: Record<TaskStatus, string> = {
  [TaskStatus.Disabled]: "Отключено",
  [TaskStatus.Wait]: "Ожидание",
  [TaskStatus.Queued]: "В очереди",
  [TaskStatus.Preparing]: "Подготовка",
  [TaskStatus.RunWaiting]: "Ожидание запуска",
  [TaskStatus.Running]: "Выполняется",
  [TaskStatus.Success]: "Успешно",
  [TaskStatus.Failed]: "Ошибка",
  [TaskStatus.Removed]: "Удалено",
};

interface StatusData {
  Status: TaskStatus;
  Count: number;
  startAngle?: number; // Опциональные поля для углов
  endAngle?: number;
}

const props = defineProps<{
  data: StatusData[];
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const hoveredData = ref<StatusData | null>(null);
const tooltipStyle = ref({ top: "0px", left: "0px" });

const total = computed(() =>
  props.data.reduce((sum, item) => sum + item.Count, 0)
);

const getStatusName = (status: TaskStatus) => {
  return STATUS_NAMES[status] || `Статус ${status}`;
};

const getPercentage = (count: number) => {
  if (total.value === 0) return 0;
  return ((count / total.value) * 100).toFixed(1);
};

// Анимация и рисование диаграммы
const drawChart = (progress: number = 1) => {
  if (!canvas.value) return;

  const ctx = canvas.value.getContext("2d");
  if (!ctx) return;

  const width = (canvas.value.width = 150);
  const height = (canvas.value.height = 150);
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = 70;
  const innerRadius = 40;

  ctx.clearRect(0, 0, width, height);

  props.data.forEach((item) => {
    if (item.startAngle === undefined || item.endAngle === undefined) return;

    const sliceAngle = item.endAngle - item.startAngle;
    const interpolatedEnd = item.startAngle + sliceAngle * progress;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, item.startAngle, interpolatedEnd);
    ctx.arc(
      centerX,
      centerY,
      innerRadius,
      interpolatedEnd,
      item.startAngle,
      true
    );
    ctx.closePath();

    ctx.fillStyle = STATUS_COLORS[item.Status] || "#ccc";
    ctx.fill();
  });
};
const calculateAngles = () => {
  let startAngle = 0; // Без -Math.PI/2

  props.data.forEach((item) => {
    const sliceAngle = (item.Count / total.value) * 2 * Math.PI;
    const endAngle = startAngle + sliceAngle;

    item.startAngle = startAngle;
    item.endAngle = endAngle;

    startAngle = endAngle;
  });
};
const animateChart = () => {
  if (!canvas.value) return;

  calculateAngles(); // <<< Сначала точно рассчитываем углы

  let animationProgress = 0;
  const animationDuration = 1000;

  const drawFrame = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / animationDuration, 1);
    animationProgress = progress;

    drawChart(animationProgress);

    if (progress < 1) {
      requestAnimationFrame(drawFrame);
    } else {
      drawChart(1); // финальный рендер
    }
  };

  let startTime: number | null = null;
  requestAnimationFrame(drawFrame);
};

// Обработка наведения мыши
const handleMouseMove = (event: MouseEvent) => {
  if (!canvas.value) return;

  const rect = canvas.value.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  const centerX = canvas.value.width / 2;
  const centerY = canvas.value.height / 2;
  const radius = 70;
  const innerRadius = 40;

  const dx = x - centerX;
  const dy = y - centerY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Угол от 0 до 2π (нормализуем)
  let angle = Math.atan2(dy, dx);
  if (angle < 0) angle += 2 * Math.PI;

  if (distance >= innerRadius && distance <= radius) {
    hoveredData.value = null;

    for (const item of props.data) {
      if (item.startAngle !== undefined && item.endAngle !== undefined) {
        const start = item.startAngle;
        const end = item.endAngle;

        const isInside =
          end >= start
            ? angle >= start && angle <= end
            : angle >= start || angle <= end; // если сектор пересекает 0

        if (isInside) {
          hoveredData.value = item;
          tooltipStyle.value = {
            top: `${event.clientY - rect.top + 10}px`,
            left: `${event.clientX - rect.left + 10}px`,
          };
          break;
        }
      }
    }
  } else {
    hoveredData.value = null;
  }
};

const handleMouseLeave = () => {
  hoveredData.value = null;
};

onMounted(() => {
  nextTick(() => animateChart());
});

watch(
  () => props.data,
  () => {
    nextTick(() => animateChart());
  },
  { deep: true }
);
</script>

<style scoped>
.chart-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

canvas {
  width: 150px;
  height: 150px;
}

.tooltip {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none; /* чтобы мышь не пересекала tooltip */
  z-index: 10;
  transform: translateY(-50%);
  min-width: 12rem;
  min-height: 3rem;
}

.tooltip-color {
  width: 0.8rem;
  min-width: 0.8rem;
  height: 0.8rem;
  min-height: 0.8rem;
  border-radius: 0.1rem;
  display: inline-block;
}
</style>
